import { textblockTypeInputRule } from "@tiptap/core"
import { Heading } from "@tiptap/extension-heading"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { toggleBlockType } from "../../util/block-node-helpers"
import { observedInputRule } from "../stock-bindings/stock-bindings-extension"

const HEADING_SHORTCUT_LEVELS = [
  { shortcut: 1, level: 3 },
  { shortcut: 2, level: 4 },
] as const

/**
 * Cp mods:
 *
 * - Heading levels are H3/H4 (H1/H2 are the lesson/card titles).
 * These are presented to the user as "Big Heading" and "Small Heading".
 * For simplicity, we allow inputting H3/H4 as if they were H1/H2 - see HEADING_SHORTCUT_LEVELS.
 * - Keyboard shortcuts use our toggleBlockType instead of toggleHeading, as in the toolbar button.
 * - Editor analytics (analogous to stock-bindings-extension.ts).
 */
export const CpHeading = Heading.extend({
  addOptions() {
    return { ...this.parent!(), levels: HEADING_SHORTCUT_LEVELS.map(({ level }) => level) }
  },

  parseHTML() {
    return [
      ...(this.parent?.() ?? []),
      ...HEADING_SHORTCUT_LEVELS.map(({ shortcut, level }) => ({
        tag: `h${shortcut}`,
        attrs: { level },
      })),
    ]
  },

  addKeyboardShortcuts() {
    return Object.fromEntries(
      HEADING_SHORTCUT_LEVELS.map(({ shortcut, level }) => [
        `Mod-Alt-${shortcut}`,
        () =>
          this.editor
            .chain()
            .label(OPERATION_CONTRACTS.OP_SET_HEADING, "keyboard", { level })
            .command(toggleBlockType("heading", "paragraph", { level }))
            .run(),
      ]),
    )
  },

  addInputRules() {
    return HEADING_SHORTCUT_LEVELS.map(({ shortcut, level }) =>
      observedInputRule(
        textblockTypeInputRule({
          find: new RegExp(`^(#{${shortcut}})\\s$`),
          type: this.type,
          getAttributes: { level },
        }),
        ({ chain }) =>
          chain().label(OPERATION_CONTRACTS.OP_SET_HEADING, "markdown", { level }).run(),
      ),
    )
  },
})
